import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { TokenService } from '@ninety/platform/services/services/token.service';

export const hasAccessTokenGuard: CanActivateFn = () => {
  const tokenService = inject(TokenService);
  const accessToken = tokenService.getAccessToken();

  if (!accessToken) {
    redirectToLogin(tokenService);
    return false;
  }

  if (tokenService.isAccessTokenExpired()) {
    return tokenService.refreshTokenApi().pipe(
      catchError(() => {
        redirectToLogin(tokenService);
        return of(false);
      }),
      map(() => true)
    );
  }

  return true;
};

function redirectToLogin(tokenService: TokenService) {
  tokenService.removeAccessToken();
  tokenService.removeEntitlementsToken();

  const { pathname, search, hash, origin } = window.location;

  // If we're already on /login, don't redirect again
  if (pathname.startsWith('/login')) return;

  // If the current URL already has a redirectUrl, preserve that as-is
  const currentParams = new URLSearchParams(search);
  const existingRedirect = currentParams.get('redirectUrl');

  // Otherwise, capture the full current route (path + query + fragment)
  const redirectTarget = existingRedirect ?? (`${pathname}${search}${hash}` || '/');

  window.location.replace(buildLoginRedirectUrl(origin, redirectTarget));
}

/**
 * Builds the `/login` redirect URL, lifting `bos` out of the encoded `redirectUrl` value onto a
 * sibling top-level param. Login branding only reads its own route's query params
 * (selectQueryParams), not redirectUrl's contents, so without this the login screen shows the
 * default brand instead of the one the user was bounced from.
 */
export function buildLoginRedirectUrl(origin: string, redirectTarget: string): string {
  const loginUrl = new URL('/login', origin);
  loginUrl.searchParams.set('redirectUrl', redirectTarget);

  try {
    const bos = new URL(redirectTarget, origin).searchParams.get('bos');
    if (bos) loginUrl.searchParams.set('bos', bos);
  } catch {
    // unparseable redirectTarget; skip lifting bos, login page falls back to default brand
  }

  return loginUrl.toString();
}
